/**
 * Вёрстка задания с сервера (T3, виды Reading как на engnovate, 28.10.2026). Формат — т.4,
 * docs/tasks/T4-mock-task-layout.md: title, frame, blocks; пропуск — {{N}}, N = место вопроса
 * в задании (`slot`). Генерация пишет её для заметок и резюме (heading / line / bullet).
 *
 * ⚠️ ВЁРСТКА ПРИНИМАЕТСЯ, ТОЛЬКО ЕСЛИ СХОДИТСЯ С ВОПРОСАМИ: каждый вопрос — ровно один {{slot}},
 * чужих {{N}} нет. Иначе null, и карточка рисует задание списком, как без вёрстки: ученик не
 * увидит пропуск без поля или вопрос без места. Сервер проверяет то же (layoutForStudent) —
 * это вторая защита. Форма и таблица пока не принимаются: генерация их не пишет, рисовать
 * их по вёрстке — отдельный шаг.
 */
import type { StudentQuestion } from '../api/ieltsMock';

export type LayoutPiece = { kind: 'text'; text: string } | { kind: 'gap'; slot: number };

export type LayoutBlock =
  | { type: 'heading'; text: string }
  | { type: 'line'; text: string }
  | { type: 'bullet'; text: string; sub: string[] };

export type ServerLayout = {
  title: string;
  frame: boolean;
  blocks: LayoutBlock[];
  bySlot: Map<number, StudentQuestion>;
};

const SLOT_RE = /\{\{(\d+)\}\}/g;

/** Текст с пропусками {{N}} — куски «текст / пропуск» по порядку. */
export function splitSlots(text: string): LayoutPiece[] {
  const out: LayoutPiece[] = [];
  let last = 0;
  for (const m of text.matchAll(SLOT_RE)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: text.slice(last, at) });
    out.push({ kind: 'gap', slot: Number(m[1]) });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Вёрстка задания или null, если её нет или она не сходится с вопросами. */
export function serverLayout(raw: unknown, questions: StudentQuestion[]): ServerLayout | null {
  if (!isObj(raw) || !Array.isArray(raw.blocks) || raw.blocks.length === 0) return null;
  const blocks: LayoutBlock[] = [];
  const texts: string[] = [];
  for (const b of raw.blocks) {
    if (!isObj(b)) return null;
    const text = str(b.text);
    if (b.type === 'heading') {
      blocks.push({ type: 'heading', text });
    } else if (b.type === 'line') {
      blocks.push({ type: 'line', text });
      texts.push(text);
    } else if (b.type === 'bullet') {
      const sub = Array.isArray(b.sub) ? b.sub.map(str).filter((s) => s !== '') : [];
      blocks.push({ type: 'bullet', text, sub });
      texts.push(text, ...sub);
    } else {
      return null; // form / table — не в этом шаге
    }
  }
  const bySlot = new Map<number, StudentQuestion>();
  for (const q of questions) {
    if (typeof q.slot !== 'number' || bySlot.has(q.slot)) return null;
    bySlot.set(q.slot, q);
  }
  const seen = new Set<number>();
  for (const t of texts) {
    for (const p of splitSlots(t)) {
      if (p.kind !== 'gap') continue;
      if (!bySlot.has(p.slot) || seen.has(p.slot)) return null;
      seen.add(p.slot);
    }
  }
  if (seen.size !== bySlot.size) return null;
  return { title: str(raw.title), frame: raw.frame === true, blocks, bySlot };
}
